import { Link } from 'react-router-dom';
import { useState, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { comics, categories } from '@/mocks/comics';

const NEWSLETTER_FORM_URL = 'https://readdy.ai/api/form/d9otp6iqs38sj6jc7fo0';

const faqData = [
  {
    question: '虚空韩漫网的漫画都是正版吗？',
    answer: '是的，虚空韩漫网作为正规的在线漫画网站，所有漫画内容均来自正版授权合作，我们在尊重版权的同时为用户提供优质的免费韩漫下拉式阅读服务。',
  },
  {
    question: '什么是下拉式阅读？',
    answer: '下拉式阅读是韩漫特有的阅读方式，您只需向下滑动页面即可连续阅读漫画内容，无需翻页。这种阅读方式更适合手机和电脑端的纵向浏览习惯，让阅读体验更加流畅自然。',
  },
  {
    question: '虚空韩漫网需要注册账号吗？',
    answer: '目前虚空韩漫网的大部分内容可以免费直接阅读，无需注册账号。注册账号后可以享受收藏漫画、评论互动、阅读记录同步等更多个性化功能。',
  },
  {
    question: '漫画更新频率是怎样的？',
    answer: '我们与各大韩漫出版社保持紧密合作，大多数连载中的漫画每周更新一话。您可以在漫画详情页查看具体的更新日期，也可以关注我们的社交媒体账号获取最新更新通知。',
  },
  {
    question: '如何反馈问题或提出建议？',
    answer: '您可以通过关于我们页面上的联系方式与我们取得联系，包括电子邮件、在线客服等渠道。我们非常重视每一位用户的反馈，会尽快处理您的问题。',
  },
];

export default function Home() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const featuredComics = comics.filter((c) => c.featured);
  const latestComics = [...comics].sort(
    (a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()
  );
  const popularComics = [...comics]
    .sort((a, b) => b.viewCount - a.viewCount)
    .slice(0, 8);

  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    try {
      const formData = new FormData(form);
      if (honeypotEl) formData.delete('website_alt');
      const res = await fetch(NEWSLETTER_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* plain text */ }
      if (res.ok && parsed && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          (parsed && (parsed as { meta?: { message?: string } }).meta?.message) ||
          (parsed && (parsed as { message?: string }).message) ||
          '';
        setFormError(serverMsg || '订阅失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
      setFormStatus('error');
    }
  };

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '虚空韩漫网',
        url: 'https://szlzjsm.cn',
        description:
          '虚空韩漫网 - 正版韩漫大全，提供免费韩漫下拉式阅读，正规在线漫画网站。',
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'contact@szlzjsm.cn',
          contactType: 'customer service',
          availableLanguage: ['Chinese'],
        },
      },
      {
        '@type': 'WebSite',
        name: '虚空韩漫网',
        url: 'https://szlzjsm.cn',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://szlzjsm.cn/search?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        name: '虚空韩漫网 - 正版韩漫大全首页',
        url: 'https://szlzjsm.cn',
        description:
          '虚空韩漫网作为正规的在线漫画网站，提供正版韩漫大全和免费韩漫下拉式阅读，在尊重版权的同时服务用户。',
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqData.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
    ],
  };

  return (
    <>
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20stylized%20artistic%20Korean%20manhwa%20themed%20banner%20with%20warm%20coral%20red%20and%20gold%20gradients%2C%20abstract%20comic%20panel%20layouts%2C%20soft%20ink%20wash%20texture%2C%20elegant%20minimalist%20composition%2C%20dreamy%20atmosphere%2C%20no%20text%20or%20characters&width=1800&height=900&seq=hero-bg-manhwa&orientation=landscape"
            alt="虚空韩漫网 - 正版韩漫大全"
            title="虚空韩漫网 - 正版韩漫大全"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4">
            <h1 className="text-2xl md:text-5xl font-heading font-bold text-background-50 mb-4 leading-tight">
              虚空韩漫网 - 正版韩漫大全
            </h1>
            <p className="text-sm md:text-lg text-background-200 max-w-2xl mb-8 leading-relaxed">
              作为正规的在线漫画网站，虚空韩漫网提供正版韩漫大全和免费韩漫下拉式阅读，在尊重版权的同时服务用户。海量热门韩漫，随时随地畅享阅读乐趣。
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <Link
                to="/category/all"
                className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                立即探索
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 rounded-full border border-background-300 text-background-50 font-medium text-sm hover:bg-background-50/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10">
            <div className="w-6 h-10 rounded-full border-2 border-background-300 flex items-start justify-center">
              <span className="block w-1 h-2 bg-background-300 rounded-full mt-2 animate-bounce"></span>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-primary-500 py-8">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { value: '10,000+', label: '漫画资源' },
                { value: '500万+', label: '月活跃用户' },
                { value: '99.9%', label: '正版覆盖率' },
                { value: '24h', label: '极速更新' },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="text-2xl md:text-3xl font-heading font-bold text-background-50">
                    {stat.value}
                  </p>
                  <p className="text-sm text-background-200 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Comics */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900">
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">精选优质韩漫，不容错过的精彩作品</p>
              </div>
              <Link
                to="/category/all"
                className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
              {featuredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900">
                漫画分类
              </h2>
              <p className="text-sm text-foreground-500 mt-1">按喜好探索不同类型的韩漫作品</p>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {categories.filter((c) => c.slug !== 'all').map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="px-6 py-2.5 rounded-full bg-background-50 border border-background-300 text-foreground-700 text-sm font-medium hover:border-primary-400 hover:text-primary-500 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900">
                  最近更新
                </h2>
                <p className="text-sm text-foreground-500 mt-1">第一时间追更你喜爱的漫画</p>
              </div>
              <Link
                to="/category/all"
                className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
              {latestComics.slice(0, 10).map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Popular Ranking */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900">
                人气排行
              </h2>
              <p className="text-sm text-foreground-500 mt-1">最受用户欢迎的韩漫作品</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {popularComics.map((comic, index) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="flex items-center gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`text-lg font-heading font-bold w-8 h-8 flex items-center justify-center rounded-full ${
                        index < 3
                          ? 'bg-accent-500 text-background-50'
                          : 'bg-background-200 text-foreground-500'
                      }`}
                    >
                      {index + 1}
                    </span>
                    <div className="w-12 h-16 rounded overflow-hidden flex-shrink-0">
                      <img
                        src={comic.coverImage}
                        alt={comic.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-heading font-bold text-foreground-900 truncate">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-foreground-400">
                      <span className="flex items-center gap-1 whitespace-nowrap">
                        <i className="ri-star-fill text-accent-500 text-xs"></i>
                        {comic.rating}
                      </span>
                      <span className="whitespace-nowrap">
                        {comic.viewCount > 10000
                          ? `${(comic.viewCount / 10000).toFixed(1)}万阅读`
                          : `${comic.viewCount}阅读`}
                      </span>
                    </div>
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                      comic.status === '连载中'
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-foreground-100 text-foreground-600'
                    }`}
                  >
                    {comic.status}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500 mt-1">关于虚空韩漫网的常见疑问解答</p>
            </div>
            <div className="flex flex-col gap-3">
              {faqData.map((faq, index) => (
                <div
                  key={index}
                  className="rounded-lg border border-background-200/70 overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between p-4 text-left bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
                    onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                  >
                    <span className="text-sm font-medium text-foreground-800">
                      {faq.question}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-300 ${
                        activeFaq === index ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  {activeFaq === index && (
                    <div className="px-4 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter */}
        <section className="py-14 md:py-20 bg-secondary-500">
          <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-background-50 mb-3">
              订阅更新通知
            </h2>
            <p className="text-sm text-background-200 mb-6">
              订阅我们的新闻通讯，第一时间获取最新漫画更新和独家优惠信息
            </p>
            <form
              ref={formRef}
              onSubmit={handleNewsletterSubmit}
              className="flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto"
              data-readdy-form=""
            >
              <div className="honeypot-wrapper">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>
              <input
                type="email"
                name="email"
                placeholder="请输入您的邮箱地址"
                required
                className="flex-1 w-full px-5 py-3 rounded-full text-sm bg-background-50 text-foreground-800 placeholder-foreground-400 border-0 focus:outline-none focus:ring-2 focus:ring-accent-400"
              />
              <button
                type="submit"
                className="px-8 py-3 rounded-full bg-accent-500 text-background-50 font-medium text-sm hover:bg-accent-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                立即订阅
              </button>
            </form>
            {formStatus === 'success' && (
              <p className="mt-3 text-sm text-accent-300">订阅成功！感谢您的关注。</p>
            )}
            {formStatus === 'error' && (
              <p className="mt-3 text-sm text-red-300">{formError}</p>
            )}
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
    </>
  );
}